import { setKeyValue, updateKeyValue, unsetKeyValue } from 'modules/keyValueStore/actions';
import { FAILURE, PENDING, SUCCESS } from 'utils/constants/queryStatuses';
import {
  createPatientFinanceProfile,
  createUserFinanceProfile,
  getMatchedFinanceProgramsPublic,
  getPatientFinanceProfile,
  getPatientMatchedFinancePrograms,
  getUserFinanceProfile,
  getUserMatchedFinancePrograms,
  updatePatientFinanceProfile,
  updateUserFinanceProfile,
} from './api';
import {
  getEmptyFinanceProfile,
  getFinanceParams,
  prepareFinanceProfileToUpsert, prepareParamsToGetPatientMatchedPrograms,
  preparePatientFinanceProfileToUpsert,
} from './utils';
import {
  PUBLIC_PROFILE_STORE_KEY,
  PUBLIC_PROGRAMS_STORE_KEY,
  getMatchedFinanceProgramsStoreKey,
  getFinanceProfileStoreKey,
} from './actionTypes';

const compareFinanceProfiles = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const exceptionToError = (ex) => {
  const { response: { status } = {}, message } = ex;
  return { responseStatus: status, message };
};

export const resetPublicFinanceAction = () => (dispatch) => {
  dispatch(unsetKeyValue(PUBLIC_PROFILE_STORE_KEY));
  dispatch(unsetKeyValue(PUBLIC_PROGRAMS_STORE_KEY));
};

export const fetchMatchedFinanceProgramsAction = ({
  userWixId,
  isHCP,
  financeParams,
}) => async (dispatch, getState) => {
  const programsStoreKey = getMatchedFinanceProgramsStoreKey(userWixId);

  const paramsId = `${isHCP}_${JSON.stringify(financeParams)}`;

  const {
    keyValueStore: {
      [programsStoreKey]: { status: currentProgramsStatus, paramsId: currentParamsId, pendingParamsId } = {},
    },
  } = getState();

  if (
    (currentProgramsStatus === SUCCESS && currentParamsId === paramsId)
    || (currentProgramsStatus === PENDING && pendingParamsId === paramsId)
  ) {
    return;
  }

  try {
    // setValue is not used to preserve current data
    dispatch(updateKeyValue(programsStoreKey, { status: PENDING, pendingParamsId: paramsId }));

    let programs;
    if (isHCP) {
      programs = await getPatientMatchedFinancePrograms(
        { userWixId, params: prepareParamsToGetPatientMatchedPrograms(financeParams) },
      );
    } else {
      programs = await getUserMatchedFinancePrograms(financeParams);
    }
    dispatch(setKeyValue(programsStoreKey, { status: SUCCESS, data: programs, paramsId }));
  } catch {
    dispatch(setKeyValue(programsStoreKey, { status: FAILURE }));
  }
};

export const fetchFinanceProfileAction = ({ userWixId, isHCP }) => async (dispatch, getState) => {
  if (!userWixId) {
    return;
  }

  const profileStoreKey = getFinanceProfileStoreKey(userWixId);

  const {
    keyValueStore: {
      [profileStoreKey]: { status: currentFinanceProfileStatus, data: currentFinanceProfile } = {},
    },
  } = getState();

  if (currentFinanceProfile || currentFinanceProfileStatus === PENDING) {
    return;
  }

  let financeProfile;
  try {
    // setValue is not used to preserve current data
    dispatch(updateKeyValue(profileStoreKey, { status: PENDING }));

    if (isHCP) {
      financeProfile = await getPatientFinanceProfile(userWixId);
    } else {
      financeProfile = await getUserFinanceProfile();
    }
    dispatch(setKeyValue(profileStoreKey, { status: SUCCESS, data: financeProfile }));
  } catch (ex) {
    dispatch(setKeyValue(profileStoreKey, { status: FAILURE, error: exceptionToError(ex) }));
  }
};

export const updateFinanceProfileAction = ({ userWixId, isHCP, data: financeProfile }) => async (dispatch) => {
  const profileStoreKey = getFinanceProfileStoreKey(userWixId);

  try {
    // setValue is not used to preserve current data
    dispatch(updateKeyValue(profileStoreKey, { status: PENDING }));
    if (isHCP) {
      await updatePatientFinanceProfile({ userWixId, data: prepareFinanceProfileToUpsert(financeProfile) });
    } else {
      await updateUserFinanceProfile(prepareFinanceProfileToUpsert(financeProfile));
    }
    dispatch(setKeyValue(profileStoreKey, { status: SUCCESS, data: financeProfile }));
  } catch (ex) {
    dispatch(setKeyValue(profileStoreKey, { status: FAILURE, error: exceptionToError(ex) }));
  }
};

export const createFinanceProfileAction = ({ userWixId, isHCP, financeProfile }) => async (dispatch) => {
  const profileStoreKey = getFinanceProfileStoreKey(userWixId);

  try {
    dispatch(setKeyValue(profileStoreKey, { status: PENDING }));

    if (isHCP) {
      // exclude userWixId
      await createPatientFinanceProfile(preparePatientFinanceProfileToUpsert(financeProfile));
    } else {
      await createUserFinanceProfile(prepareFinanceProfileToUpsert(financeProfile));
    }
    dispatch(setKeyValue(profileStoreKey, { status: SUCCESS, data: financeProfile }));

    dispatch(resetPublicFinanceAction());
  } catch (ex) {
    dispatch(setKeyValue(profileStoreKey, { status: FAILURE, error: exceptionToError(ex) }));
  }
};

// public actions
export const fetchPublicMatchedFinanceProgramsAction = ({ financeParams }) => async (dispatch, getState) => {
  const paramsId = JSON.stringify(financeParams);

  const {
    keyValueStore: {
      [PUBLIC_PROGRAMS_STORE_KEY]: { status: currentProgramsStatus, paramsId: currentParamsId, pendingParamsId } = {},
    },
  } = getState();

  if (
    (currentProgramsStatus === SUCCESS && currentParamsId === paramsId)
    || (currentProgramsStatus === PENDING && pendingParamsId === paramsId)
  ) {
    return;
  }

  try {
    // setValue is not used to preserve current data
    dispatch(updateKeyValue(PUBLIC_PROGRAMS_STORE_KEY, { status: PENDING, pendingParamsId: paramsId }));

    const programs = await getMatchedFinanceProgramsPublic(financeParams);
    dispatch(setKeyValue(PUBLIC_PROGRAMS_STORE_KEY, { status: SUCCESS, data: programs, paramsId }));
  } catch {
    dispatch(setKeyValue(PUBLIC_PROGRAMS_STORE_KEY, { status: FAILURE }));
  }
};

export const setPublicFinanceProfileAction = ({ user }) => async (dispatch, getState) => {
  const {
    keyValueStore: {
      [PUBLIC_PROFILE_STORE_KEY]: { data: currentFinanceProfile } = {},
    },
  } = getState();
  const { aidTypes: currentAidTypes } = currentFinanceProfile ?? {};

  const financeProfile = getFinanceParams(
    user ?? { condition_profile: {}, personal: { condition: null } },
    currentFinanceProfile ?? getEmptyFinanceProfile(),
    currentAidTypes ?? [],
  );

  if (currentFinanceProfile && compareFinanceProfiles(currentFinanceProfile, financeProfile)) {
    return;
  }

  dispatch(setKeyValue(PUBLIC_PROFILE_STORE_KEY, { status: SUCCESS, data: financeProfile }));
};

export const updatePublicFinanceProfileAction = ({ data }) => async (dispatch) => {
  dispatch(setKeyValue(PUBLIC_PROFILE_STORE_KEY, { status: SUCCESS, data }));
};
